import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Plus, Archive, Users, DoorOpen, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Organization Departments management (release-candidate patch §4A).
 *
 * Owners + Admins may create, rename, archive, and restore. All other roles
 * see the list read-only via the department picker in profile / office studio.
 */
export default function DepartmentsView() {
  const { currentOrg, memberships } = useAuth();
  const myRole = (memberships || []).find((m) => m.org_id === currentOrg?.id)?.role;
  const canEdit = myRole === "owner" || myRole === "admin";

  const [rows, setRows] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/departments", { params: { include_archived: showArchived } });
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load departments");
      setRows([]);
    }
  };
  useEffect(() => { load(); }, [currentOrg?.id, showArchived]);

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.post("/departments", { name: newName.trim(), description: newDesc.trim() || null });
      toast.success(`Department "${newName.trim()}" added.`);
      setNewName(""); setNewDesc("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to create department");
    } finally { setBusy(false); }
  };

  const startEdit = (r) => { setEditingId(r.id); setEditName(r.name); setEditDesc(r.description || ""); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); setEditDesc(""); };
  const saveEdit = async () => {
    try {
      await api.patch(`/departments/${editingId}`, { name: editName.trim(), description: editDesc });
      cancelEdit();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const archive = async (r) => {
    const warn = r.member_count > 0 || r.room_count > 0
      ? `\n\nWarning: ${r.member_count} member(s) and ${r.room_count} room(s) still reference this department.`
      : "";
    if (!window.confirm(`Archive department "${r.name}"? It will be hidden from new selections.${warn}`)) return;
    try {
      await api.delete(`/departments/${r.id}`);
      toast.success("Department archived.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const restore = async (r) => {
    try {
      await api.patch(`/departments/${r.id}`, { active: true });
      toast.success("Department restored.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (rows === null) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="departments-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0 gap-3">
        <Building2 className="w-4 h-4 text-[var(--brand-accent)]" />
        <h1 className="text-lg font-semibold">Departments</h1>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 border rounded-full px-2 py-0.5">
          {currentOrg?.name}
        </span>
        <label className="ml-auto flex items-center gap-1 text-xs text-zinc-600">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="dept-show-archived" />
          Show archived
        </label>
      </div>

      {canEdit && (
        <form onSubmit={create} className="border-b bg-zinc-50 px-6 py-3 flex items-center gap-2" data-testid="dept-create-form">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Department name" className="w-56" data-testid="dept-create-name" required />
          <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Description (optional)" className="flex-1" data-testid="dept-create-description" />
          <Button type="submit" size="sm" disabled={busy || !newName.trim()} data-testid="dept-create-submit">
            <Plus className="w-4 h-4 mr-1"/> Add
          </Button>
        </form>
      )}

      <div className="flex-1 overflow-auto scroll-thin">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="dept-empty">
            No departments yet.{canEdit && " Add the first one above."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b sticky top-0 bg-white">
              <tr>
                <th className="px-6 py-2">Department</th>
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2">Members</th>
                <th className="px-2 py-2">Rooms</th>
                <th className="px-2 py-2">Status</th>
                {canEdit && <th className="px-2 py-2 text-right pr-6">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-zinc-50" data-testid={`dept-row-${r.id}`}>
                  <td className="px-6 py-2 font-medium">
                    {editingId === r.id ? (
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" data-testid={`dept-edit-name-${r.id}`}/>
                    ) : (
                      <span data-testid={`dept-name-${r.id}`}>{r.name}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-zinc-600">
                    {editingId === r.id ? (
                      <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="h-8" data-testid={`dept-edit-desc-${r.id}`}/>
                    ) : (
                      <span>{r.description || "—"}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-zinc-600">
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3"/>{r.member_count || 0}</span>
                  </td>
                  <td className="px-2 py-2 text-zinc-600">
                    <span className="inline-flex items-center gap-1"><DoorOpen className="w-3 h-3"/>{r.room_count || 0}</span>
                  </td>
                  <td className="px-2 py-2">
                    {r.active
                      ? <span className="text-[10px] font-semibold uppercase tracking-widest bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">Active</span>
                      : <span className="text-[10px] font-semibold uppercase tracking-widest bg-zinc-200 text-zinc-500 rounded-full px-2 py-0.5">Archived</span>}
                  </td>
                  {canEdit && (
                    <td className="px-2 py-2 text-right pr-6">
                      {editingId === r.id ? (
                        <div className="inline-flex gap-1">
                          <Button size="sm" onClick={saveEdit} data-testid={`dept-save-${r.id}`}>Save</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      ) : r.active ? (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => startEdit(r)} data-testid={`dept-edit-${r.id}`}>Rename</Button>
                          <Button size="sm" variant="outline" onClick={() => archive(r)} data-testid={`dept-archive-${r.id}`}><Archive className="w-3 h-3 mr-1"/>Archive</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => restore(r)} data-testid={`dept-restore-${r.id}`}><Undo2 className="w-3 h-3 mr-1"/>Restore</Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!canEdit && rows.length > 0 && (
        <div className="border-t bg-amber-50 text-amber-800 text-xs px-6 py-2 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5"/>
          You have read-only access. Only owners and admins can manage departments.
        </div>
      )}
    </div>
  );
}
